import { useMemo, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Plus, Award, AlertTriangle } from 'lucide-react'
import DataTable, { Column } from '@/components/DataTable'

interface EquityCredit {
  id: number
  agent_id: number
  agent_name: string
  credit_amount: number
  credit_type: string
  event_name: string
  event_date: string
  notes: string | null
  source_type: string | null
  source_id: number | null
  granted_by_name: string | null
  created_at: string
}

interface Summary {
  total_credits: number
  this_year: number
  agents_with_credits: number
  by_type: {
    production: number
    override: number
    bonus: number
    adjustment: number
  }
}

interface AtRiskAgent {
  agent_id: number
  agent_name: string
  contract_signed: boolean
  pending_count: number
  pending_total: number
  expired_count: number
  expired_total: number
  earliest_expires_at: string | null
  expired_latest_at: string | null
}

interface Props extends PageProps {
  credits: EquityCredit[]
  summary: Summary
  at_risk_agents: AtRiskAgent[]
}

export default function EquityIndex({ credits, summary, at_risk_agents }: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const [riskFilter, setRiskFilter] = useState<'all' | 'pending' | 'expired'>('all')

  const filteredAtRisk = useMemo(() => {
    if (riskFilter === 'pending') return at_risk_agents.filter((a) => a.pending_count > 0)
    if (riskFilter === 'expired') return at_risk_agents.filter((a) => a.expired_count > 0)
    return at_risk_agents
  }, [at_risk_agents, riskFilter])

  const totalPending = at_risk_agents.reduce((s, a) => s + a.pending_count, 0)
  const totalExpired = at_risk_agents.reduce((s, a) => s + a.expired_count, 0)
  const typeColors: Record<string, string> = {
    production: 'bg-green-100 text-green-700',
    override: 'bg-blue-100 text-blue-700',
    bonus: 'bg-purple-100 text-purple-700',
    adjustment: 'bg-orange-100 text-orange-700'
  }

  const columns: Column<EquityCredit>[] = [
    {
      key: 'agent_name',
      header: 'Agent',
      sortable: true,
      render: (credit) => (
        <Link href={`/agents/${credit.agent_id}`} className="font-medium text-chestnut-forest hover:text-chestnut-gold">
          {credit.agent_name}
        </Link>
      )
    },
    {
      key: 'event_name',
      header: 'Event',
      sortable: true,
      render: (credit) => (
        <div>
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-chestnut-gold" />
            <span className="text-sm text-chestnut-charcoal">{credit.event_name}</span>
          </div>
          {credit.notes && (
            <p className="text-xs text-chestnut-charcoal/60 mt-1">{credit.notes}</p>
          )}
        </div>
      )
    },
    {
      key: 'credit_type',
      header: 'Type',
      sortable: true,
      render: (credit) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${typeColors[credit.credit_type]}`}>
          {credit.credit_type}
        </span>
      )
    },
    {
      key: 'credit_amount',
      header: 'Credits',
      sortable: true,
      className: 'text-right font-semibold text-chestnut-forest',
      headerClassName: 'text-right',
      render: (credit) => `+${credit.credit_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      key: 'event_date',
      header: 'Date',
      sortable: true,
      render: (credit) => new Date(credit.event_date).toLocaleDateString()
    },
    {
      key: 'granted_by_name',
      header: 'Source',
      sortable: true,
      className: 'text-chestnut-charcoal/60',
      render: (credit) => credit.source_type === 'Commission' ? 'Auto (Commission)' : (credit.granted_by_name || 'Manual')
    }
  ]

  return (
    <AppLayout>
      <Head title="Equity Credits" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Equity Credits</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Track and manage agent equity credits</p>
          </div>
          {isAdmin && (
            <Link href="/equity_credits/grant" className="btn-primary flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Grant Credits
            </Link>
          )}
        </div>

        {isAdmin && (
          <div className="flex gap-2 border-b border-chestnut-gray">
            <Link
              href="/equity_credits"
              className="px-4 py-2 text-sm font-medium border-b-2 border-chestnut-forest text-chestnut-forest"
            >
              Credits
            </Link>
            <Link
              href="/equity_credits/pool"
              className="px-4 py-2 text-sm font-medium text-chestnut-charcoal/70 hover:text-chestnut-forest"
            >
              Pool Summary
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card border-l-4 border-l-chestnut-gold">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Total Credits</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.total_credits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="card border-l-4 border-l-chestnut-forest">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">This Year</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.this_year.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="card border-l-4 border-l-blue-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Agents</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.agents_with_credits}</p>
          </div>
          <div className="card border-l-4 border-l-purple-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Bonus</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{summary.by_type.bonus.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>

        {at_risk_agents.length > 0 && (
          <div className="card p-6 border-l-4 border-l-orange-400">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <div>
                  <h2 className="text-lg font-semibold text-chestnut-charcoal font-heading">
                    Agents at Risk of Forfeiting Equity
                  </h2>
                  <p className="text-sm text-chestnut-charcoal/60">
                    {totalPending} pending commission{totalPending === 1 ? '' : 's'} awaiting contract signature
                    {totalExpired > 0 && `, ${totalExpired} already forfeited`}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {(['all', 'pending', 'expired'] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setRiskFilter(opt)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full capitalize ${
                      riskFilter === opt
                        ? 'bg-chestnut-forest text-white'
                        : 'bg-chestnut-cream text-chestnut-charcoal/70 hover:bg-chestnut-gray/50'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-chestnut-charcoal/10">
                  <tr className="text-left text-sm text-chestnut-charcoal/60">
                    <th className="pb-3 font-medium">Agent</th>
                    <th className="pb-3 font-medium">Contract</th>
                    <th className="pb-3 font-medium text-right" title="Number of pending commission events and the dollar total of equity credits being held until contract signature.">
                      Pending Commissions (Credits $)
                    </th>
                    <th className="pb-3 font-medium text-right" title="Number of forfeited commission events and the dollar total of equity credits lost because the contract wasn't signed in time.">
                      Forfeited Commissions (Credits $)
                    </th>
                    <th className="pb-3 font-medium">Earliest Expiration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-chestnut-charcoal/5">
                  {filteredAtRisk.map((a) => (
                    <tr
                      key={a.agent_id}
                      className="hover:bg-chestnut-cream/50 cursor-pointer"
                      onClick={() => router.visit(`/agents/${a.agent_id}`)}
                    >
                      <td className="py-3 font-medium text-chestnut-forest">{a.agent_name}</td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          a.contract_signed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {a.contract_signed ? 'Signed' : 'Unsigned'}
                        </span>
                      </td>
                      <td className="py-3 text-right text-sm">
                        {a.pending_count > 0 ? (
                          <span title={`${a.pending_count} pending commission${a.pending_count === 1 ? '' : 's'} totaling $${a.pending_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in equity credits, pending contract signature.`}>
                            {a.pending_count} ({a.pending_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 text-right text-sm text-red-600">
                        {a.expired_count > 0 ? (
                          <span title={`${a.expired_count} forfeited commission${a.expired_count === 1 ? '' : 's'} totaling $${a.expired_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in equity credits lost to expiration.`}>
                            {a.expired_count} ({a.expired_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 text-sm">
                        {a.earliest_expires_at
                          ? new Date(a.earliest_expires_at).toLocaleDateString()
                          : a.expired_latest_at
                            ? <span className="text-red-600">Expired {new Date(a.expired_latest_at).toLocaleDateString()}</span>
                            : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAtRisk.length === 0 && (
                <p className="text-chestnut-charcoal/60 py-6 text-center text-sm">No agents match this filter.</p>
              )}
            </div>
          </div>
        )}

        <div className="card p-6">
          <DataTable
            data={credits}
            columns={columns}
            searchPlaceholder="Search equity credits..."
            searchKeys={['agent_name', 'event_name', 'credit_type']}
            pageSize={10}
            emptyMessage="No equity credits recorded yet."
            getRowKey={(credit) => credit.id}
            onRowClick={(credit) => router.visit(`/equity_credits/${credit.id}`)}
          />
        </div>
      </div>
    </AppLayout>
  )
}
